import Image from 'next/image';

import { BlockTabsFragment } from '~/server/generated/contentful-graphql';

import { ContentfulTabs } from './main';

export function ImpactTabs() {
  return (
    <ContentfulTabs
      block={block as unknown as BlockTabsFragment}
      headlineClassName='text-[22px] lg:text-[48px] lg:leading-[56px]'
      renderContent={(tab: TabWithImpact) => (
        <div className='mt-10 space-y-8'>
          <div className='grid grid-cols-2 gap-4 md:gap-8'>
            <ImpactSection title='Happiness' items={tab.impact.happiness} />
            <ImpactSection title='Healing' items={tab.impact.healing} />
          </div>
        </div>
      )}
    />
  );
}

interface ImpactItem {
  icon: string;
  text: string;
}

interface Impact {
  happiness: ImpactItem[];
  healing: ImpactItem[];
}

interface TabWithImpact {
  headline: string;
  image?: {
    url: string;
    description: string;
  };
  impact: Impact;
  copy?: null;
  ctaTitlePrimary?: string;
  ctaLinkPrimary?: string;
}

interface BlockTabsWithImpact {
  __typename: 'BlockTabs';
  tabsCollection: {
    items: TabWithImpact[];
  };
}

// Common icon definitions
const HAPPINESS_ICONS = {
  comfort: { icon: '/img/impact/icons/comfort.svg', text: 'Comfort' },
  socialization: {
    icon: '/img/impact/icons/socialization.svg',
    text: 'Socialization',
  },
  normalization: {
    icon: '/img/impact/icons/normalization.svg',
    text: 'Normalization',
  },
  play: { icon: '/img/impact/icons/play.svg', text: 'Play' },
  entertainment: {
    icon: '/img/impact/icons/entertainment.svg',
    text: 'Entertainment',
  },
};

const HEALING_ICONS = {
  proceduralSupport: {
    icon: '/img/impact/icons/procedural-support.svg',
    text: 'Procedural Support',
  },
  emotionalSupport: {
    icon: '/img/impact/icons/emotional-support.svg',
    text: 'Emotional Support',
  },
  education: { icon: '/img/impact/icons/education.svg', text: 'Education' },
  painManagement: {
    icon: '/img/impact/icons/pain-management.svg',
    text: 'Pain Management',
  },
  physicalTherapy: {
    icon: '/img/impact/icons/physical-therapy.svg',
    text: 'Physical Therapy',
  },
};

// Reusable component for impact sections
function ImpactSection({ title, items }: { title: string; items: ImpactItem[] }) {
  return (
    <div>
      <h3 className='font-greycliff-extrabold text-muted-foreground mb-5 text-xl md:text-3xl'>{title}</h3>
      <div className='space-y-4 lg:space-y-8'>
        {items.map((item: ImpactItem, idx: number) => (
          <div key={idx} className='flex items-center gap-4 md:gap-6'>
            <Image src={item.icon} alt={item.text} width={40} height={40} className='size-5 md:size-10' />
            <span className='text-sm md:text-lg'>{item.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const block: BlockTabsWithImpact = {
  __typename: 'BlockTabs',
  tabsCollection: {
    items: [
      {
        headline: 'Starlight Gaming',
        image: {
          url: '/img/impact/gaming.jpg',
          description: 'Gaming program impact',
        },
        impact: {
          happiness: [
            HAPPINESS_ICONS.play,
            HAPPINESS_ICONS.entertainment,
            HAPPINESS_ICONS.socialization,
            HAPPINESS_ICONS.normalization,
          ],
          healing: [
            HEALING_ICONS.emotionalSupport,
            HEALING_ICONS.painManagement,
            HEALING_ICONS.education,
            HEALING_ICONS.physicalTherapy,
          ],
        },
        ctaTitlePrimary: 'Learn more',
        ctaLinkPrimary: '/gaming',
      },
      {
        headline: 'Starlight Hospital Gowns',
        image: {
          url: '/img/impact/hospital-wear.jpg',
          description: 'Hospital wear program',
        },
        impact: {
          happiness: [HAPPINESS_ICONS.comfort, HAPPINESS_ICONS.socialization, HAPPINESS_ICONS.normalization],
          healing: [HEALING_ICONS.proceduralSupport, HEALING_ICONS.emotionalSupport],
        },
        ctaTitlePrimary: 'Learn more',
        ctaLinkPrimary: '/hospital-gowns',
      },
      {
        headline: 'Starlight Toy Deliveries',
        image: {
          url: '/img/impact/deliveries.jpg',
          description: 'Delivery program impact',
        },
        impact: {
          happiness: [HAPPINESS_ICONS.play, HAPPINESS_ICONS.entertainment, HAPPINESS_ICONS.normalization],
          healing: [HEALING_ICONS.emotionalSupport, HEALING_ICONS.education],
        },
        ctaTitlePrimary: 'Learn more',
        ctaLinkPrimary: '/toy-deliveries',
      },
      {
        headline: 'Radio Flyer Hero Wagons',
        image: {
          url: '/img/impact/hero-wagons.jpg',
          description: 'Hero wagons program',
        },
        impact: {
          happiness: [
            HAPPINESS_ICONS.play,
            HAPPINESS_ICONS.entertainment,
            HAPPINESS_ICONS.socialization,
            HAPPINESS_ICONS.normalization,
          ],
          healing: [HEALING_ICONS.emotionalSupport, HEALING_ICONS.physicalTherapy],
        },
        ctaTitlePrimary: 'Learn more',
        ctaLinkPrimary: '/wagons',
      },
      {
        headline: 'Starlight Families',
        image: {
          url: '/img/impact/starlight-families.jpg',
          description: 'Starlight families program',
        },
        impact: {
          happiness: [HAPPINESS_ICONS.play, HAPPINESS_ICONS.entertainment, HAPPINESS_ICONS.comfort],
          healing: [
            HEALING_ICONS.proceduralSupport,
            HEALING_ICONS.emotionalSupport,
            HEALING_ICONS.painManagement,
            HEALING_ICONS.education,
          ],
        },
        ctaTitlePrimary: 'Learn more',
        ctaLinkPrimary: '/families',
      },
    ],
  },
};
